import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Clock, Users, MapPin } from 'lucide-react';
import { fetchRideTypes, RIDE_TYPE_IMAGES } from '../utils/rideTypes';
import MapView from '../components/MapView';
import PaymentModal from '../components/PaymentModal';
import CityWaitlistModal from '../components/city/CityWaitlistModal';

const SelectRide = () => {
  const navigate = useNavigate();
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [stops, setStops] = useState([]);  // Array of stops
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [route, setRoute] = useState(null);
  const [selectedRide, setSelectedRide] = useState(null);
  const [rideTypes, setRideTypes] = useState([]);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash'); // 'cash' or 'stripe'
  const [waitlist, setWaitlist] = useState(null); // { city, service } when city gate fires

  useEffect(() => {
    const pickupLoc = localStorage.getItem('ride_pickup');
    const dropoffLoc = localStorage.getItem('ride_dropoff');
    const stopsData = localStorage.getItem('ride_stops');
    
    if (!pickupLoc || !dropoffLoc) {
      navigate('/');
      return;
    }
    
    setPickup(pickupLoc);
    setDropoff(dropoffLoc);
    if (stopsData) {
      try {
        setStops(JSON.parse(stopsData));
      } catch (e) {
        console.error('Error parsing stops:', e);
      }
    }
    
    // Check if returning from Stripe payment AFTER setting pickup/dropoff
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      // User returned from Stripe, wait for component to load then open modal
      const rideId = localStorage.getItem('current_ride_id');
      if (rideId) {
        // Small delay to ensure ride types are loaded
        setTimeout(() => {
          setShowPaymentModal(true);
        }, 500);
      }
    }
    
    // Fetch ride types and get route with real coordinates
    const loadData = async () => {
      try {
        const api = (await import('../utils/api')).default;
        const types = await fetchRideTypes();
        setRideTypes(types);
        
        // Geocode pickup location
        const pickupResponse = await api.get('/maps/geocode/forward', {
          params: { query: pickupLoc }
        });
        
        if (!pickupResponse.data.results || pickupResponse.data.results.length === 0) {
          toast({
            title: 'Location Not Found',
            description: `Cannot find pickup location: "${pickupLoc}". Please enter a more specific address or use a suggested location.`,
            variant: 'destructive',
          });
          setLoading(false);
          return;
        }
        
        const pickupResult = pickupResponse.data.results[0];
        setPickupCoords({
          longitude: pickupResult.longitude,
          latitude: pickupResult.latitude
        });
        
        // Geocode dropoff location
        const dropoffResponse = await api.get('/maps/geocode/forward', {
          params: { query: dropoffLoc }
        });
        
        if (!dropoffResponse.data.results || dropoffResponse.data.results.length === 0) {
          toast({
            title: 'Location Not Found',
            description: `Cannot find dropoff location: "${dropoffLoc}". Please enter a more specific address or use a suggested location.`,
            variant: 'destructive',
          });
          setLoading(false);
          return;
        }
        
        const dropoffResult = dropoffResponse.data.results[0];
        setDropoffCoords({
          longitude: dropoffResult.longitude,
          latitude: dropoffResult.latitude
        });
        
        // Get route from Mapbox
        const routeResponse = await api.get('/maps/directions', {
              params: {
                start_lng: pickupResult.longitude,
                start_lat: pickupResult.latitude,
                end_lng: dropoffResult.longitude,
                end_lat: dropoffResult.latitude,
                mode: 'driving'
          }
        });
        
        if (routeResponse.data.route) {
          setRoute(routeResponse.data.route);
          
          // Calculate prices for all ride types using backend API
          const pricesObj = {};
          
          for (const type of types) {
            try {
              const priceResponse = await api.post('/rides/calculate-price', {
                    pickup: pickupLoc,
                    dropoff: dropoffLoc,
                    stops: stops,  // Include stops in calculation
                    rideTypeId: type.id
              });
              
              pricesObj[type.id] = {
                price: priceResponse.data.price,
                baseFare: priceResponse.data.baseFare,
                stopsFee: priceResponse.data.stopsFee,
                waitTimeFee: priceResponse.data.waitTimeFee,
                numStops: priceResponse.data.numStops,
                distance: priceResponse.data.distance,
                duration: priceResponse.data.duration,
                surgeMultiplier: priceResponse.data.surgeMultiplier,
                surgeActive: priceResponse.data.surgeActive,
                surgeReason: priceResponse.data.surgeReason,
                preSurgePrice: priceResponse.data.preSurgePrice,
              };
              
              // Debug log to check if stops data is being received
              console.log(`Price data for ${type.id}:`, pricesObj[type.id]);
            } catch (priceError) {
              console.error(`Error calculating price for ${type.id}:`, priceError);
              // Fallback calculation for this type
              const distanceKm = routeResponse.data.metadata.distance_km;
              const basePrice = 3.50;
              const perKm = 3.00;
              const price = ((basePrice + (distanceKm * perKm)) * type.priceMultiplier).toFixed(2);
              pricesObj[type.id] = {
                price,
                distance: distanceKm,
                duration: routeResponse.data.metadata.duration_minutes
              };
            }
          }
          
          setPrices(pricesObj);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading ride data:', error);
        
        // Fallback: Set default prices if geocoding/routing fails
        const types = await fetchRideTypes();
        setRideTypes(types);
        const fallbackDistance = 8.0; // Default 8 km
        const pricesObj = {};
        
        types.forEach(type => {
          const basePrice = 3.50;
          const perKm = 3.00;
          const price = ((basePrice + (fallbackDistance * perKm)) * type.priceMultiplier).toFixed(2);
          pricesObj[type.id] = {
            price,
            distance: fallbackDistance,
            duration: 15
          };
        });
        
        setPrices(pricesObj);
        setLoading(false);
      }
    };
    
    loadData();
  }, [navigate]);

  const handleConfirmRide = async () => {
    if (!selectedRide) {
      alert('Please select a ride type');
      return;
    }

    try {
      const api = (await import('../utils/api')).default;
      const priceData = prices[selectedRide.id];
      
      // Check if this is a scheduled ride
      const isScheduleMode = localStorage.getItem('ride_schedule_mode') === 'true';
      const scheduledDateTime = localStorage.getItem('ride_scheduled_datetime');
      
      const bookingData = {
        pickup,
        dropoff,
        pickupLocation: pickupCoords ? {
          latitude: pickupCoords.latitude,
          longitude: pickupCoords.longitude
        } : null,
        dropoffLocation: dropoffCoords ? {
          latitude: dropoffCoords.latitude,
          longitude: dropoffCoords.longitude
        } : null,
        stops: stops,  // Include stops in booking
        rideTypeId: selectedRide.id,
        price: parseFloat(priceData.price),
        distance: parseFloat(priceData.distance),
        paymentMethod: paymentMethod
      };
      
      // Add scheduledAt if in schedule mode
      if (isScheduleMode && scheduledDateTime) {
        bookingData.scheduledAt = new Date(scheduledDateTime).toISOString();
      }
      
      const response = await api.post('/rides/book', bookingData);
      
      // Store ride ID
      localStorage.setItem('current_ride_id', response.data.ride.id);
      
      // For cash payments, skip payment modal and go directly to tracking
      if (paymentMethod === 'cash') {
        navigate('/tracking');
      } else {
        // For Stripe payments, open payment modal
        setShowPaymentModal(true);
      }
    } catch (error) {
      console.error('Error booking ride:', error);
      // City gate — instead of a scary alert, open the launch waitlist
      if (error?.response?.status === 403) {
        const detail = error.response?.data?.detail || '';
        if (/city|aren't live|isn't live|launching/i.test(detail)) {
          // Extract city from the pickup string (last comma segment or any seeded city)
          const parts = (pickup || '').split(',').map(s => s.trim()).filter(Boolean);
          const guessCity = parts[parts.length - 1] || 'this city';
          setWaitlist({ city: guessCity, service: 'rides' });
          return;
        }
      }
      alert('Failed to book ride. Please try again.');
    }
  };
  
  const handlePaymentSuccess = async () => {
    // Wait for ride to be fully updated with payment status before navigating
    const rideId = localStorage.getItem('current_ride_id');
    if (!rideId) {
      navigate('/tracking');
      return;
    }

    try {
      const api = (await import('../utils/api')).default;
      let attempts = 0;
      const maxAttempts = 10;
      const pollInterval = 1000; // 1 second

      const pollRideStatus = async () => {
        try {
          const response = await api.get(`/rides/${rideId}`);
          const ride = response.data.ride;
          
          // Check if payment status has been updated
          if (ride.payment_status === 'paid' || ride.payment_status === 'completed') {
            // Payment confirmed, safe to navigate
            navigate('/tracking');
            return true;
          }
          
          attempts++;
          if (attempts >= maxAttempts) {
            // Timeout reached, navigate anyway
            console.warn('Payment status polling timeout, navigating anyway');
            navigate('/tracking');
            return true;
          }
          
          // Continue polling
          return false;
        } catch (error) {
          console.error('Error polling ride status:', error);
          attempts++;
          if (attempts >= maxAttempts) {
            // Error or timeout, navigate anyway
            navigate('/tracking');
            return true;
          }
          return false;
        }
      };

      // Start polling
      const poll = async () => {
        const done = await pollRideStatus();
        if (!done) {
          setTimeout(poll, pollInterval);
        }
      };
      
      poll();
    } catch (error) {
      console.error('Error in handlePaymentSuccess:', error);
      // Fallback: navigate anyway
      navigate('/tracking');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Map */}
          <div className="order-2 lg:order-1">
            <Card className="h-[500px] lg:h-[600px] overflow-hidden">
              {loading || !pickupCoords || !dropoffCoords ? (
                <div className="relative w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading map...</p>
                  </div>
                </div>
              ) : (
                <MapView
                  pickup={pickupCoords}
                  dropoff={dropoffCoords}
                  route={route}
                  showRoute={true}
                  height="100%"
                />
              )}
            </Card>
          </div>

          {/* Right: Ride selection */}
          <div className="order-1 lg:order-2 space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Choose a ride</h1>
              <p className="text-gray-600">Select the best option for your trip</p>
            </div>

            {/* Payment Method Selector */}
            <Card className="p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Payment Method</h3>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setPaymentMethod('stripe')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    paymentMethod === 'stripe'
                      ? 'border-pink-600 bg-pink-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-1">💳</div>
                    <p className="font-medium text-sm">Card Payment</p>
                    <p className="text-xs text-gray-600 mt-1">Pay now with card</p>
                  </div>
                </button>
                <button
                  onClick={() => setPaymentMethod('cash')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    paymentMethod === 'cash'
                      ? 'border-pink-600 bg-pink-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-1">💵</div>
                    <p className="font-medium text-sm">Cash Payment</p>
                    <p className="text-xs text-gray-600 mt-1">Pay driver directly</p>
                  </div>
                </button>
              </div>
              {paymentMethod === 'cash' && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-800">
                    <strong>Note:</strong> You'll pay the driver in cash at the end of your ride.
                  </p>
                </div>
              )}
            </Card>

            {/* Scheduled Ride Indicator */}
            {localStorage.getItem('ride_schedule_mode') === 'true' && (
              <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                    <Clock className="h-5 w-5 text-pink-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-pink-900">Scheduled Ride</p>
                    <p className="text-sm text-pink-700">
                      {new Date(localStorage.getItem('ride_scheduled_datetime')).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-6">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading ride options...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {rideTypes.map((ride) => {
                    const priceData = prices[ride.id] || {};
                    const price = priceData.price || '0.00';
                    const isSelected = selectedRide?.id === ride.id;

                    return (
                    <Card
                      key={ride.id}
                      className={`p-4 cursor-pointer transition-all hover:shadow-lg ${
                        isSelected ? 'ring-2 ring-pink-600 bg-pink-50' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedRide(ride)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-20 h-14 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                            <img 
                              src={ride.image || RIDE_TYPE_IMAGES[ride.id] || RIDE_TYPE_IMAGES.standard} 
                              alt={ride.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = RIDE_TYPE_IMAGES.standard;
                              }}
                            />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg text-gray-900">{ride.name}</h3>
                            <p className="text-sm text-gray-600">{ride.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                              <div className="flex items-center gap-1" data-testid={`ride-distance-${ride.id}`}>
                                <MapPin className="h-4 w-4" />
                                <span>{priceData.distance ? `${Number(priceData.distance).toFixed(1)} km` : '—'}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Users className="h-4 w-4" />
                                <span>{ride.capacity} seats</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-gray-900">K{price}</div>
                          <div className="text-sm text-gray-500">per ride</div>
                          {priceData.surgeActive && (
                            <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold" data-testid={`surge-badge-${ride.id}`} title={priceData.surgeReason || 'Surge pricing in effect'}>
                              ⚡ {priceData.surgeMultiplier}× SURGE
                            </div>
                          )}
                          {priceData.numStops > 0 && (
                            <div className="mt-2 text-xs text-gray-600 space-y-1">
                              <div>Base: K{priceData.baseFare?.toFixed(2)}</div>
                              <div>+{priceData.numStops} stop{priceData.numStops > 1 ? 's' : ''}: K{priceData.stopsFee?.toFixed(2)}</div>
                              <div>Wait time: K{priceData.waitTimeFee?.toFixed(2)}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
                </div>
              )}

                <Button
                onClick={handleConfirmRide}
                disabled={!selectedRide || loading}
                className="w-full bg-pink-600 hover:bg-pink-700 text-white py-6 text-lg font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm {selectedRide?.name || 'Ride'}
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Payment Modal */}
      <PaymentModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        rideData={selectedRide ? {
          id: localStorage.getItem('current_ride_id'),
          pickup,
          dropoff,
          rideType: selectedRide,
          price: prices[selectedRide.id]?.price || '0.00'
        } : (pickup && dropoff) ? {
          id: localStorage.getItem('current_ride_id'),
          pickup,
          dropoff,
          rideType: { name: 'Ride' },
          price: '0.00'
        } : null}
        onPaymentSuccess={handlePaymentSuccess}
      />

      {/* City launch waitlist (shown when the city gate blocks booking) */}
      <CityWaitlistModal
        open={!!waitlist}
        onClose={() => setWaitlist(null)}
        city={waitlist?.city}
        service={waitlist?.service}
      />
    </div>
  );
};

export default SelectRide;